import type { AccentHue } from "@repo/fika-design-system/color"
import type { IconName } from "@repo/fika-design-system/icon"
import { debounce } from "@solid-primitives/scheduled"
import { createMemo, createSignal, type ParentProps, Show } from "solid-js"
import { Mini } from "../components/mini/mini"
import { Tooltip } from "../components/tooltip"
import { formClass } from "./search-form.css"

type Filter = {
  values: {
    value: string
    count?: number
    iconName?: IconName
    hue?: AccentHue
  }[]
  value: string
}

type Tag = {
  values: {
    value: string
    count?: number
  }[]
  value: string
}

type Sort = {
  values: { value: string; count?: number }[]
  value: string
}

type Props = {
  query?: string
  onChange: (change: {
    query?: string
    filter?: string
    sort?: string
    tag?: string
  }) => void
  filter?: Filter
  tag?: Tag
  sort?: Sort
} & ParentProps
export default function SearchForm(props: Props) {
  const [query, setQuery] = createSignal(decodeURIComponent(props.query || ""))

  const debouncedOnChange = debounce((query: string) => {
    props.onChange({
      query,
      filter: props.filter?.value,
      tag: props.tag?.value,
      sort: props.sort?.value,
    })
  }, 200)

  return (
    <form class={formClass} onSubmit={ev => ev.preventDefault()}>
      <Mini.Menu>
        <Mini.Section>
          <Mini.Icon name="search" />
          <Tooltip label="search">
            <Mini.TextField
              placeholder="search"
              value={query()}
              onChange={query => {
                setQuery(query)
                debouncedOnChange(query)
              }}
            />
          </Tooltip>
        </Mini.Section>
        <Show when={props.filter}>
          {filter => (
            <FilterSelect
              filter={filter()}
              onChange={filter => {
                props.onChange({
                  query: props.query,
                  filter: filter!,
                  tag: props.tag?.value,
                  sort: props.sort?.value,
                })
              }}
            />
          )}
        </Show>
        <Show when={props.tag}>
          {tag => (
            <TagSelect
              tag={tag()}
              onChange={tag => {
                props.onChange({
                  query: props.query,
                  filter: props.filter?.value,
                  tag: tag!,
                  sort: props.sort?.value,
                })
              }}
            />
          )}
        </Show>
        <Show when={props.sort}>
          {sort => (
            <SortSelect
              sort={sort()}
              onChange={sort => {
                props.onChange({
                  query: props.query,
                  filter: props.filter?.value,
                  tag: props.tag?.value,
                  sort: sort!,
                })
              }}
            />
          )}
        </Show>
      </Mini.Menu>
    </form>
  )
}

function FilterSelect(props: {
  filter: Filter
  onChange: (value: string | null) => void
}) {
  const values = createMemo(() => props.filter.values)
  const value = createMemo(() => props.filter.value)
  const items = createMemo(() =>
    values().map(value => ({
      ...value,
      label: value.value,
    })),
  )

  return (
    <Mini.Section>
      <Tooltip
        label="filter"
        hue={values().find(v => v.value === value())?.hue}
      >
        <Mini.Select
          items={items()}
          value={value()}
          onChange={props.onChange}
        />
      </Tooltip>
    </Mini.Section>
  )
}

function TagSelect(props: {
  tag: Tag
  onChange: (value: string | null) => void
}) {
  const values = createMemo(() => props.tag.values)
  const value = createMemo(() => props.tag.value)

  return (
    <Mini.Section>
      <Tooltip label="tag">
        <Mini.Select
          fixedIcon="tag"
          items={values().map(value => ({
            ...value,
            hue: "sand",
            label: value.value,
          }))}
          value={value()}
          onChange={props.onChange}
        />
      </Tooltip>
    </Mini.Section>
  )
}

function SortSelect(props: {
  sort: Sort
  onChange: (value: string | null) => void
}) {
  const values = createMemo(() => props.sort.values)
  const value = createMemo(() => props.sort.value)

  return (
    <Mini.Section>
      <Tooltip label="sort">
        <Mini.Select
          items={values().map(value => ({
            value: value.value,
            label: value.value,
          }))}
          value={value()}
          fixedIcon="arrow-down-wide-narrow"
          onChange={props.onChange}
        />
      </Tooltip>
    </Mini.Section>
  )
}
